/**
 * Group sheet SKUs by Box Group for shop listings and variant pickers.
 * Purchasable unit remains each row's Box id — grouping is presentation only.
 */

/** Stable group key: shared Box Group name, or standalone Box id when empty. */
export function getProductGroupKey(product) {
  const group = String(product?.boxGroup || "").trim();
  return group || String(product?.id || "");
}

/**
 * Cluster key for shop listings / variant siblings.
 * Uses Box Group when set; otherwise product name so size variants share one card.
 */
export function getListingClusterKey(product) {
  const boxGroup = String(product?.boxGroup || "").trim();
  if (boxGroup) return `bg:${boxGroup.toLowerCase()}`;
  const name = String(product?.name || "").trim();
  if (name) return `nm:${name.toLowerCase()}`;
  return `id:${String(product?.id || "")}`;
}

/**
 * Cluster key when grouping shop listings by size — always by dimensions
 * so all colors at 10×8×4 share one card (Box Group does not split sizes).
 */
export function getSizeClusterKey(product) {
  const size = String(product?.boxSizeLabel || product?.fabric || "").trim();
  if (size) return `sz:${size.toLowerCase()}`;
  return `id:${String(product?.id || "")}`;
}

export function getSizeLabel(product) {
  return String(product?.boxSizeLabel || product?.fabric || "").trim();
}

/** Variants for product detail / add-to-cart when listings are grouped by size. */
export function getVariantsForDetail(product, allProducts, groupBy = "none") {
  if (!product || !Array.isArray(allProducts)) return product ? [product] : [];

  if (groupBy === "size") {
    const sizeLabel = getSizeLabel(product);
    if (!sizeLabel) return [product];
    return allProducts.filter((p) => getSizeLabel(p) === sizeLabel);
  }

  const cluster = allProducts.filter(
    (p) => getListingClusterKey(p) === getListingClusterKey(product),
  );

  if (groupBy === "color") {
    return cluster.filter((p) => p.color === product.color);
  }

  return cluster.length ? cluster : [product];
}

/** All SKUs in the same name/box-group cluster (legacy helper). */
export function getVariantsInGroup(product, allProducts) {
  return getVariantsForDetail(product, allProducts, "none");
}

function pickRepresentativeVariant(variants) {
  if (!variants?.length) return null;
  const inStock = variants.find((v) => (v.stock || 0) > 0);
  return inStock || variants[0];
}

/** First in-stock variant, or first row — for cards and modals. */
export function pickListingRepresentative(variants) {
  return pickRepresentativeVariant(variants);
}

/** Whether add-to-cart should prompt for size (color grouping) or color (size grouping). */
export function listingNeedsVariantPick(entry) {
  if (!entry || entry.groupBy === "none" || entry.variants.length <= 1) return false;
  if (entry.groupBy === "color") return getVariantSizes(entry.variants).length > 1;
  if (entry.groupBy === "size") return getVariantColors(entry.variants).length > 1;
  return false;
}

function uniqueValues(variants, field) {
  const seen = new Set();
  const out = [];
  for (const v of variants) {
    const val = String(v[field] || "").trim();
    if (!val || seen.has(val)) continue;
    seen.add(val);
    out.push(val);
  }
  return out;
}

/**
 * Build shop listing entries from flat SKU rows.
 * @param {object[]} products
 * @param {"none"|"color"|"size"} groupBy
 * @returns {{ listingKey: string, representative: object, variants: object[], groupBy: string, displayColor?: string, displaySize?: string }[]}
 */
export function buildShopListings(products, groupBy = "none") {
  if (!Array.isArray(products) || !products.length) return [];

  if (groupBy === "none") {
    return products.map((p) => ({
      listingKey: String(p.id),
      representative: p,
      variants: [p],
      groupBy: "none",
      displayColor: p.color,
      displaySize: p.boxSizeLabel || p.fabric,
    }));
  }

  const clusterKeyFor = (p) =>
    groupBy === "size" ? getSizeClusterKey(p) : getListingClusterKey(p);

  const byGroup = new Map();
  for (const p of products) {
    const gk = clusterKeyFor(p);
    if (!byGroup.has(gk)) byGroup.set(gk, []);
    byGroup.get(gk).push(p);
  }

  const listings = [];

  for (const [groupKey, groupVariants] of byGroup) {
    if (groupBy === "color") {
      const colors = uniqueValues(groupVariants, "color");
      for (const color of colors) {
        const colorVariants = groupVariants.filter((v) => v.color === color);
        const rep = pickRepresentativeVariant(colorVariants);
        if (!rep) continue;
        listings.push({
          listingKey: `${groupKey}::color::${color}`,
          representative: rep,
          variants: colorVariants,
          groupBy: "color",
          displayColor: color,
          displaySize: null,
        });
      }
      continue;
    }

    if (groupBy === "size") {
      const size = getSizeLabel(groupVariants[0]) || groupKey;
      const rep = pickRepresentativeVariant(groupVariants);
      if (!rep) continue;
      listings.push({
        listingKey: `${groupKey}::size`,
        representative: rep,
        variants: groupVariants,
        groupBy: "size",
        displayColor: null,
        displaySize: size,
      });
      continue;
    }
  }

  return listings;
}

/** Unique box size labels for a variant set. */
export function getVariantSizes(variants) {
  return uniqueValues(variants, "boxSizeLabel");
}

/** Unique colors for a variant set. */
export function getVariantColors(variants) {
  return uniqueValues(variants, "color");
}

/** Sizes available for a color within a variant set. */
export function sizesForColor(variants, color) {
  return uniqueValues(
    variants.filter((v) => v.color === color),
    "boxSizeLabel",
  );
}

/** Colors available for a size within a variant set. */
export function colorsForSize(variants, sizeLabel) {
  const out = [];
  const seen = new Set();
  for (const v of variants) {
    const sz = v.boxSizeLabel || v.fabric;
    if (sz !== sizeLabel) continue;
    const c = String(v.color || "").trim();
    if (!c || seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}

/** Find SKU matching color + size label. */
export function findVariant(variants, { color, sizeLabel }) {
  return (
    variants.find(
      (v) =>
        v.color === color &&
        (v.boxSizeLabel || v.fabric) === sizeLabel,
    ) || null
  );
}
